import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Table, Button, Modal, Input, Form, Space, Tag, Switch } from 'antd';
import { SearchOutlined, UserOutlined, MailOutlined, PhoneOutlined, EnvironmentOutlined, EditOutlined } from '@ant-design/icons';
import ReactPaginate from 'react-paginate';
import BASEURL from '../API/Config';

const Customer = () => {
  const token = localStorage.getItem('token');
  const [customers, setCustomers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form] = Form.useForm();

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASEURL}/user/all-users`, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setCustomers(data);
      setFiltered(data);
    } catch {
      Swal.fire('Error', 'Failed to load customers.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCustomers(); }, []);

  const handleSearch = (e) => {
    const q = e.target.value.toLowerCase();
    setSearch(q);
    setFiltered(customers.filter(c =>
      (c.firstname || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.phoneNumber || '').toString().includes(q)
    ));
  };

  const openModal = (customer) => {
    setEditId(customer._id);
    form.setFieldsValue({
      firstname: customer.firstname || '',
      lastname: customer.lastname || '',
      email: customer.email || '',
      phoneNumber: customer.phoneNumber || '',
      address1: customer.address1 || '',
      pincode: customer.pincode || '',
      isBlocked: !customer.isBlocked,
    });
    setShowModal(true);
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const res = await fetch(`${BASEURL}/user/edit-user/${editId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...values, isBlocked: !values.isBlocked }),
      });
      if (!res.ok) throw new Error(await res.text());
      Swal.fire('Updated!', 'Customer updated successfully!', 'success');
      setShowModal(false);
      fetchCustomers();
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: '#',
      key: 'index',
      width: 60,
      render: (_, __, i) => <span style={{ color: '#6b7280' }}>{i + 1}</span>,
    },
    {
      title: 'CUSTOMER',
      key: 'name',
      render: (r) => (
        <Space>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#e8f1ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2874f0', fontWeight: 700, fontSize: 14 }}>
            {(r.firstname || '?')[0].toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 600, color: '#212121', fontSize: 13 }}>{r.firstname} {r.lastname}</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>{r.email}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'PHONE',
      dataIndex: 'phoneNumber',
      key: 'phone',
      render: (v) => <span style={{ color: '#374151' }}>{v || '—'}</span>,
    },
    {
      title: 'PINCODE',
      dataIndex: 'pincode',
      key: 'pincode',
      render: (v) => <span style={{ color: '#374151' }}>{v || '—'}</span>,
    },
    {
      title: 'STATUS',
      key: 'status',
      render: (r) => r.isBlocked
        ? <Tag color="red" style={{ borderRadius: 50, fontWeight: 600 }}>Inactive</Tag>
        : <Tag color="green" style={{ borderRadius: 50, fontWeight: 600 }}>Active</Tag>,
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      width: 100,
      render: (r) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => openModal(r)}
          style={{ color: '#2874f0', borderColor: '#2874f0', borderRadius: 4 }} />
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>Masters</div>
          <h2 style={{ margin: 0, fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 700, color: '#212121' }}>Customer Master</h2>
        </div>
        <div style={{ background: '#e8f1ff', borderRadius: 8, padding: '8px 16px', display: 'flex', gap: 8, alignItems: 'center' }}>
          <UserOutlined style={{ color: '#2874f0' }} />
          <span style={{ fontWeight: 700, color: '#2874f0', fontSize: 16 }}>{customers.length}</span>
          <span style={{ fontSize: 13, color: '#6b7280' }}>Total Customers</span>
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e8ecf0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 12 }}>
          <Input prefix={<SearchOutlined style={{ color: '#9ca3af' }} />} placeholder="Search name, email, phone..."
            value={search} onChange={handleSearch} style={{ width: 300, borderRadius: 4 }} />
          <div style={{ marginLeft: 'auto', fontSize: 13, color: '#6b7280' }}>
            Showing: <strong style={{ color: '#212121' }}>{filtered.length}</strong>
          </div>
        </div>
        <Table columns={columns} dataSource={filtered} rowKey="_id" loading={loading}
          pagination={{ pageSize: 10, showTotal: (t) => `${t} customers` }} size="middle" />
      </div>

      <Modal
        title={<span style={{ fontFamily: 'Outfit,sans-serif', fontWeight: 700 }}>Edit Customer</span>}
        open={showModal} onCancel={() => setShowModal(false)} footer={null} centered width={580}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="firstname" label="First Name">
              <Input prefix={<UserOutlined />} size="large" />
            </Form.Item>
            <Form.Item name="lastname" label="Last Name">
              <Input prefix={<UserOutlined />} size="large" />
            </Form.Item>
            <Form.Item name="email" label="Email">
              <Input prefix={<MailOutlined />} size="large" />
            </Form.Item>
            <Form.Item name="phoneNumber" label="Phone">
              <Input prefix={<PhoneOutlined />} size="large" />
            </Form.Item>
            <Form.Item name="address1" label="Address">
              <Input size="large" />
            </Form.Item>
            <Form.Item name="pincode" label="Pincode">
              <Input prefix={<EnvironmentOutlined />} size="large" />
            </Form.Item>
          </div>
          <Form.Item name="isBlocked" label="Status" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" style={{ background: '#2874f0' }} />
          </Form.Item>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading}
              style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, fontWeight: 600 }}>
              Update Customer
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default Customer;
